import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePinDocumentMutation } from "web_myplann/Api/Document/usePinDocumentMutation";
import { useUnpinDocumentMutation } from "web_myplann/Api/Document/useUnpinDocumentMutation";
import type {
	DocumentDTO,
	PortfolioDocumentListItemDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PinChoiceDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinChoiceDrawer";
import { PinExistingDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinExistingDrawer";
import { PinExistingOptionRow } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinExistingOptionRow";
import { PinNewDocumentRow } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinNewDocumentRow";
import type { PinCandidate } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { usePinDrawerStep } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/usePinDrawerStep";
import { mapDocumentLinkToIds } from "web_myplann/Portfolio/Helpers/mapDocumentLinkToIds";
import type { DocumentLink } from "web_myplann/Portfolio/Types/documentLink";

type Step = "choose" | "existing";

type Props = {
	open: boolean;
	onClose: () => void;
	link: DocumentLink;
	documents: PortfolioDocumentListItemDTO[];
	candidates: DocumentDTO[];
	isLoading: boolean;
};

export const DocumentPinDrawer: FC<Props> = ({
	open,
	onClose,
	link,
	documents,
	candidates,
	isLoading,
}) => {
	const { t } = useResource();
	const navigate = useNavigate();
	const { step, setStep } = usePinDrawerStep<Step>(open, "choose");

	const { mutate: pinDocument, isPending: isPinning } =
		usePinDocumentMutation();
	const { mutate: unpinDocument, isPending: isUnpinning } =
		useUnpinDocumentMutation();

	const pinnedItems = useMemo(
		() =>
			documents.map((document) => ({
				id: document.id,
				title: document.title,
				canUnpin: document.canUnpin,
			})),
		[documents],
	);
	const hasAdvisorDocument = documents.some((document) => !document.canUnpin);

	const pinCandidates = useMemo<PinCandidate[]>(
		() => candidates.map((data) => ({ kind: "document", data })),
		[candidates],
	);

	const unpin = (documentId: number) => {
		unpinDocument(documentId, {
			onSuccess: (document) =>
				toast.success(
					t(Resources.DataCard.documentUnpinnedToast, {
						name: document.title,
					}),
				),
		});
	};

	const selectCandidate = (candidate: PinCandidate) => {
		if (candidate.kind !== "document") {
			return;
		}

		pinDocument(
			{ documentId: candidate.data.id, link: mapDocumentLinkToIds(link) },
			{
				onSuccess: () => {
					toast.success(
						t(Resources.DataCard.documentPinnedToast, {
							name: candidate.data.title,
						}),
					);
					onClose();
				},
			},
		);
	};

	return (
		<>
			<PinChoiceDrawer
				kind="document"
				open={open && step === "choose"}
				onClose={onClose}
				pinnedItems={pinnedItems}
				isPending={isPinning || isUnpinning}
				footnote={
					hasAdvisorDocument
						? t(Resources.PortfolioDetail.Pin.advisorDocumentsFootnote)
						: undefined
				}
				onUnpin={unpin}
			>
				{candidates.length > 0 && (
					<PinExistingOptionRow
						kind="document"
						isLast={false}
						onClick={() => setStep("existing")}
					/>
				)}
				<PinNewDocumentRow
					onClick={() =>
						navigate(
							Routing.Portfolio.DocumentUpload.path(mapDocumentLinkToIds(link)),
						)
					}
				/>
			</PinChoiceDrawer>
			<PinExistingDrawer
				kind="document"
				open={open && step === "existing"}
				onClose={onClose}
				candidates={pinCandidates}
				isLoading={isLoading}
				isPending={isPinning}
				onSelect={selectCandidate}
			/>
		</>
	);
};
